"use client";

import { useMemo } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Read from the token rather than hardcoded, so the plot flips with the page:
 * `.dark .assessments-surface` lightens `--color-marketing-blue` to #4d6fff,
 * because the brand blue is 2.3:1 on the dark background.
 */
const BLUE = "var(--color-marketing-blue)";

/** SVG gradient id for the area fill. One chart per page, so a constant is safe. */
const FILL_ID = "sittings-area-fill";

export interface SittingBucketPoint {
  /** Start of the hour as an ISO 8601 string, UTC. */
  hourStart: string;
  sittings: number;
}

interface LiveSittingsChartProps {
  buckets: SittingBucketPoint[];
}

interface ChartPoint {
  label: string;
  /** Hours that have finished. Null on the current hour so the solid line stops. */
  settled: number | null;
  /** The current hour, plus the hour before it so the dashed run connects. */
  live: number | null;
}

// Labelled in UTC, matching the buckets the server built. Rendering these in the
// viewer's zone would put the hour labels out of step with the counts beside them.
const hourLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });

/**
 * Recharts clones this with its own props, so it is declared at module scope
 * rather than inline on `content`: a function component defined during render is
 * a fresh type on every tick, which remounts the tooltip and trips
 * `react(no-unstable-nested-components)`.
 */
function SittingTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { payload?: ChartPoint }[];
  label?: string | number;
}) {
  if (!active || !payload?.length) {
    return null;
  }
  const point = payload[0]?.payload;
  const value = point?.settled ?? point?.live ?? 0;
  const inProgress = point?.settled === null;
  return (
    <div className="border-marketing-border text-marketing-text bg-marketing-surface rounded-lg border px-2.5 py-1.5 text-xs shadow-sm">
      <div className="font-medium">
        {value} {value === 1 ? "sitting" : "sittings"}
      </div>
      <div className="text-marketing-text-muted font-mono">
        {String(label)} UTC{inProgress ? " · in progress" : ""}
      </div>
    </div>
  );
}

/**
 * Sittings per hour, split into a settled run and a live one.
 *
 * The last bucket is the hour in progress, so it is always partial and always
 * dips. Drawing it in the same solid stroke as the finished hours would read as
 * a real fall in activity, so it gets its own dashed series. `settled` and `live`
 * overlap on the boundary hour, which is what joins the two runs; without that
 * shared point the dashed segment would float, disconnected.
 */
export function LiveSittingsChart({ buckets }: LiveSittingsChartProps) {
  const points = useMemo<ChartPoint[]>(() => {
    const lastIndex = buckets.length - 1;
    return buckets.map((bucket, index) => ({
      label: hourLabel(bucket.hourStart),
      settled: index === lastIndex ? null : bucket.sittings,
      live: index >= lastIndex - 1 ? bucket.sittings : null,
    }));
  }, [buckets]);

  // Headroom above the peak, so the line reads as a trend in a plot rather than
  // filling the panel. At 1.25 a spiky series swung the full height and touched
  // both edges, which is what made the chart shout; 1.6 keeps the same shape
  // lower in the frame. A flat all-zero series would collapse onto the axis and
  // read as a bug, so it gets a nominal ceiling instead.
  const peak = Math.max(...buckets.map((bucket) => bucket.sittings), 0);
  const ceiling = peak === 0 ? 1 : Math.ceil(peak * 1.6);

  return (
    <ResponsiveContainer height="100%" width="100%">
      <ComposedChart
        data={points}
        margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
      >
        <defs>
          {/* The flat 12% fill the design specifies was measured against a grey
              panel. On white it reads as a heavy block, so it fades downward
              instead: strongest under the line, gone by the panel's bottom edge. */}
          <linearGradient id={FILL_ID} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={BLUE} stopOpacity={0.18} />
            <stop offset="100%" stopColor={BLUE} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <XAxis dataKey="label" hide />
        <YAxis domain={[0, ceiling]} hide />
        <Tooltip content={<SittingTooltip />} />
        <Area
          dataKey="settled"
          fill={`url(#${FILL_ID})`}
          isAnimationActive={false}
          stroke={BLUE}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.2}
          type="linear"
        />
        <Line
          dataKey="live"
          dot={false}
          isAnimationActive={false}
          stroke={BLUE}
          strokeDasharray="6 5"
          strokeLinecap="round"
          strokeWidth={2.2}
          type="linear"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
